import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useEffect } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
  ToggleSwitch
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import { useIdempotentCreate, fingerprintProperties, mutationInput } from "../../utils/idempotency";

const entityQuery = gql`
  query service_sectionList($service_section_list_request: service_section_list_request) {
    service_section(service_section_list_request: $service_section_list_request) {
      edges {
        id
        name
        status
        sequence
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation service_sectionMutate($service_section_mutation_request: MutationInput) {
    service_section(service_section_mutation_request: $service_section_mutation_request) {
      id
      isNew
    }
  }
`;

export default function ServiceSectionEdit({ id: entityId, action, navigate }) {
  
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);
  // variables for entity query const
  let variables = {
    service_section_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
  };

  const [name, setName] = useState('');
  const [sequence, setSequence] = useState();
  const [status, setStatus] = useState(isEdit ? 0 : 1);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const createProperties = [
    { key: "name", string: name },
    { key: "sequence", string: sequence },
    { key: "status", number: status ? 1 : 0 },
  ];
  const { idempotencyKey, isSaving, trackSave } = useIdempotentCreate({
    enabled: !isEdit,
    fingerprint: fingerprintProperties(createProperties),
  });

  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.service_section;
      const list = listResponse?.edges ?? [];
      const service_section = list[0];
      setName(service_section.name);
      setSequence(service_section.sequence);
      setStatus(service_section.status);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const validateForm = () => {
    const reasons = [];

    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (sequence === undefined || sequence === null || sequence === "") reasons.push("Order (Sequence) is required.");
    if (isNaN(Number(sequence))) reasons.push("Order (Sequence) must be a valid number.");

    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  // sets up ServiceSection mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      service_section_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: createProperties,
        idempotencyKey,
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      addNotification("good", "Service Section saved successfully");
      let id = response?.service_section?.id;
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });
  
  useEffect(() => {
    refetchEntity();
  }, [entityId])

  let loadingMutation = mutateInfo.loading;

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          {isEdit ? "Edit" : "Add"} Service Section Detail
        </span> 
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Status:</label>
            <ToggleSwitch
              checked={status}
              onChange={() => setStatus(!status)}
              size={{
                width: 60,
                height: 32,
                knob: 24,
                padding: 4
              }}
            />
          </div>
          <div className="edit-property">
            <label>Name *:</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="edit-property">
            <label>Order *:</label>
            <input value={sequence} onChange={(e) => setSequence(e.target.value)} />
          </div>
        </div>
        {invalidReasons.length > 0 && (
          <InvalidReasons reasons={invalidReasons} />
        )}
        <div className="edit-section">
          <SaveButton
            saving={!isEdit && isSaving}
            disabled={isEdit && loadingMutation}
            onClick={() => {
              if (!validateForm()) return;
              if (isEdit) mutateFunction();
              else trackSave(() => mutateFunction());
            }}
          />
        </div>
      </div>
    </>
  );
}
